import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Tooltip, DatePicker, Dropdown, Menu } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StockCountService } from '../../../Services/Manage/StockCountService';
import { UserService } from '../../../Services/Manage/UserService';
import {
    EditOutlined, EyeOutlined, SnippetsOutlined, ClockCircleOutlined,
    CheckCircleOutlined, CalendarOutlined, CloseCircleOutlined,
    DownloadOutlined, PlusOutlined, FilterOutlined, DownOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const PERMISSION_LINK = '/inventory/stock-count';
const { RangePicker } = DatePicker;

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const STATUS_COLORS = {
    "Completed": "#52c41a",
    "In Progress": "#1677ff",
    "Scheduled": "#722ed1",
    "Cancelled": "#f5222d",
};

const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#1677ff', marginTop: 3, cursor: 'pointer' }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const StockCountDashboard = () => {
    const perm = getPermission(PERMISSION_LINK);
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allCnt, setAllCnt] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userMap, setUserMap] = useState({});
    const [dateFilterRange, setDateFilterRange] = useState(null); // null = All Time (default)
    const [dateFilterLabel, setDateFilterLabel] = useState('All Time');

    const getAllCnt = async () => {
        setLoading(true);
        try {
            const request = await StockCountService.getAllCnt();
            setAllCnt(request.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getUserMap = async () => {
        try {
            const request = await UserService.getAllUsers();
            const map = {};
            (request.data || []).forEach((u) => {
                if (u.user_id) map[String(u.user_id)] = u.name;
            });
            setUserMap(map);
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getAllCnt();
        getUserMap();
    }, []);

    const visibleCnt = useMemo(
        () => perm.view_all ? allCnt : allCnt.filter((s) => String(s.added_by) === String(currentUserId)),
        [allCnt, perm.view_all, currentUserId]
    );

    // Date-filtered base for stat cards/charts — reacts to the "More Actions"
    // date preset only, not search/status/warehouse/count_date range.
    const dateFilteredCnt = useMemo(() => {
        if (!dateFilterRange) return visibleCnt;
        return visibleCnt.filter((s) => {
            const d = dayjs(s.created_at);
            return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
        });
    }, [visibleCnt, dateFilterRange]);

    const filteredCnt = useMemo(() => {
        let filtered = visibleCnt;
        if (searchText) {
            filtered = filtered.filter((s) =>
                Object.values(s).some((value) => value?.toString().toLowerCase().includes(searchText.toLowerCase()))
            );
        }
        if (selectedType) filtered = filtered.filter((s) => s.count_type === selectedType);
        if (selectedWarehouse) filtered = filtered.filter((s) => s.warehouse === selectedWarehouse);
        if (selectedStatus) filtered = filtered.filter((s) => s.status === selectedStatus);
        if (dateRange && dateRange.length === 2) {
            filtered = filtered.filter((s) => {
                if (!s.count_date) return false;
                const d = dayjs(s.count_date);
                return d.isAfter(dateRange[0].startOf('day')) && d.isBefore(dateRange[1].endOf('day'));
            });
        }
        if (dateFilterRange) {
            filtered = filtered.filter((s) => {
                const d = dayjs(s.created_at);
                return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
            });
        }
        return filtered;
    }, [visibleCnt, searchText, selectedStatus, selectedType, selectedWarehouse, dateRange, dateFilterRange]);

    const totalCnt = visibleCnt.length; // always all-time
    const completedCount = useMemo(() => dateFilteredCnt.filter((s) => s.status === "Completed").length, [dateFilteredCnt]);
    const inProgressCount = useMemo(() => dateFilteredCnt.filter((s) => s.status === "In Progress").length, [dateFilteredCnt]);
    const scheduledCount = useMemo(() => dateFilteredCnt.filter((s) => s.status === "Scheduled").length, [dateFilteredCnt]);
    const cancelledCount = useMemo(() => dateFilteredCnt.filter((s) => s.status === "Cancelled").length, [dateFilteredCnt]);

    const statusSummaryData = useMemo(() => {
        const counts = { "Completed": completedCount, "In Progress": inProgressCount, "Scheduled": scheduledCount, "Cancelled": cancelledCount };
        return Object.entries(counts).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] }));
    }, [completedCount, inProgressCount, scheduledCount, cancelledCount]);
    const statusSummaryTotal = statusSummaryData.reduce((a, b) => a + b.value, 0);

    const thisMonthStats = useMemo(() => {
        const now = new Date();
        const monthCnt = visibleCnt.filter((s) => {
            if (!s.count_date) return false;
            const d = new Date(s.count_date);
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        });
        const totalValue = monthCnt.reduce((sum, s) => sum + Number(s.total_variance_value || 0), 0);
        const positive = monthCnt.filter((s) => Number(s.total_variance_value || 0) > 0).reduce((sum, s) => sum + Number(s.total_variance_value || 0), 0);
        const negative = monthCnt.filter((s) => Number(s.total_variance_value || 0) < 0).reduce((sum, s) => sum + Number(s.total_variance_value || 0), 0);
        return { total: monthCnt.length, totalValue, positive, negative };
    }, [visibleCnt]);

const typeSummaryData = useMemo(() => {
        const counts = {};
        dateFilteredCnt.forEach((s) => {
            const key = s.count_type || "Unknown";
            counts[key] = (counts[key] || 0) + 1;
        });
        const colorMap = { "Full Stock Count": "#1677ff", "Cycle Count": "#faad14" };
        return Object.entries(counts).map(([name, value]) => ({ name, value, color: colorMap[name] || "#8c8c8c" }));
    }, [dateFilteredCnt]);
    const typeSummaryTotal = typeSummaryData.reduce((a, b) => a + b.value, 0);

    const recentCnt = useMemo(
        () => dateFilteredCnt.slice().sort((a, b) => new Date(b.count_date) - new Date(a.count_date)).slice(0, 5),
        [dateFilteredCnt]
    );

    const typeOptions = [...new Set(visibleCnt.map((s) => s.count_type).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const warehouseOptions = [...new Set(visibleCnt.map((s) => s.warehouse).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const statusOptions = ["Scheduled", "In Progress", "Completed", "Cancelled"].map((s) => ({ label: s, value: s }));


    const REPORT_COLUMNS = [
        { label: "Count No." },
        { label: "Warehouse" },
        { label: "Count Type" },
        { label: "Status" },
        { label: "Count Date" },
        { label: "Created By" },
        { label: "Total Items" },
        { label: "Variance (Rs.)" },
    ];

    const buildReportRows = () => filteredCnt.map((r) => ({
        "Count No.": r.count_no || "-",
        "Warehouse": r.warehouse || "-",
        "Count Type": r.count_type || "-",
        "Status": r.status || "-",
        "Count Date": r.count_date ? new Date(r.count_date).toLocaleDateString('en-GB') : '-',
        "Created By": userMap[String(r.added_by)] || r.added_by || '-',
        "Total Items": r.total_items ?? 0,
        "Variance (Rs.)": r.status === "Completed" ? Number(r.total_variance_value || 0) : '-',
    }));

    const dateStamp = () => dayjs().format("YYYY-MM-DD");

    const handleExportExcel = () => {
        const rows = buildReportRows();
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stock Count Report");
        XLSX.writeFile(wb, `Stock-Count-Report-${dateStamp()}.xlsx`);
    };

    const handleExportPDF = () => {
        const rows = buildReportRows();
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Stock Count Report", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
        autoTable(doc, {
            startY: 26,
            head: [REPORT_COLUMNS.map((c) => c.label)],
            body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 88, 180] },
        });
        doc.save(`Stock-Count-Report-${dateStamp()}.pdf`);
    };

    const exportMenuItems = [
        { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
        { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
    ];

    const columns = [
        { title: '#', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        { title: 'Count No.', dataIndex: 'count_no', key: 'count_no' },
        { title: 'Warehouse', dataIndex: 'warehouse', key: 'warehouse', render: (v) => v || '-' },
        { title: 'Count Type', dataIndex: 'count_type', key: 'count_type', render: (v) => v || '-' },
        {
            title: 'Status', key: 'status',
            render: (_, record) => (
                <span style={{
                    background: `${STATUS_COLORS[record.status] || '#8c8c8c'}1a`,
                    color: STATUS_COLORS[record.status] || '#8c8c8c',
                    padding: '3px 10px', borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{record.status || '-'}</span>
            ),
        },
        { title: 'Count Date', dataIndex: 'count_date', key: 'count_date', render: (v) => v ? new Date(v).toLocaleDateString('en-GB') : '-' },
        { title: 'Created By', dataIndex: 'added_by', key: 'added_by', render: (v) => userMap[String(v)] || v || '' },
        { title: 'Total Items', key: 'total_items', align: 'center', render: (_, record) => record.total_items ?? 0 },
        {
            title: 'Variance (₹)', key: 'total_variance_value', align: 'right',
            render: (_, record) => {
                if (record.status !== "Completed") return '-';
                const v = Number(record.total_variance_value || 0);
                return <span style={{ color: v >= 0 ? '#52c41a' : '#f5222d', fontWeight: 600 }}>{v >= 0 ? '+' : ''}{v.toLocaleString('en-IN')}</span>;
            },
        },
        {
            title: 'Action', key: 'Action', fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/stock-count/view-stock-count', { state: { id: record.id, action: 'View', enableEdit: false, from: '/inventory/stock-count' } })}
                            icon={<EyeOutlined />} size="middle" disabled={!perm.read}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/stock-count/edit-stock-count', { state: { id: record.id, action: 'Edit', enableEdit: true, from: '/inventory/stock-count' } })}
                            icon={<EditOutlined />} size="middle" disabled={!perm.edit}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Stock Count</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Stock Count</Typography.Text>
                </div>
       <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                    </Dropdown>
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu
                                onClick={({ key }) => {
                                    let range = null;
                                    let label = 'All Time';
                                    if (key === 'today') {
                                        range = [dayjs().startOf('day'), dayjs().endOf('day')];
                                        label = 'Today';
                                    } else if (key === 'last7') {
                                        range = [dayjs().subtract(6, 'day').startOf('day'), dayjs()];
                                        label = 'Last 7 Days';
                                    } else if (key === 'last30') {
                                        range = [dayjs().subtract(29, 'day').startOf('day'), dayjs()];
                                        label = 'Last 30 Days';
                                    } else if (key === 'lastMonth') {
                                        range = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                                        label = 'Last Month';
                                    } else if (key === 'thisYear') {
                                        range = [dayjs().startOf('year'), dayjs()];
                                        label = 'This Year';
                                    } else if (key === 'all') {
                                        range = null;
                                        label = 'All Time';
                                    }
                                    setDateFilterRange(range);
                                    setDateFilterLabel(label);
                                }}
                                items={[
                                    { key: 'all', label: <span><CalendarOutlined style={{ marginRight: 6 }} />All Time</span> },
                                    { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                                    { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                                    { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                                    { key: 'lastMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last Month</span> },
                                    { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                                ]}
                            />
                        }
                    >
                        <Button size="middle" icon={<DownOutlined />}>
                            More Actions ({dateFilterLabel})
                        </Button>
                    </Dropdown>
                    <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                        <Button
                            size="middle" type="primary" disabled={!perm.add}
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/inventory/stock-count/add-stock-count', { state: { action: 'Add', enableEdit: true, from: '/inventory/stock-count' } })}
                            style={{ background: '#1677ff', borderColor: '#1677ff' }}
                        >
                            New Stock Count
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Total Counts" value={totalCnt} sub="View all counts" color="#1658b4" bg="#eef4ff" icon={<SnippetsOutlined />} />                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Completed" value={completedCount} sub="View completed" color="#52c41a" bg="#f6ffed" icon={<CheckCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="In Progress" value={inProgressCount} sub="View in progress" color="#1677ff" bg="#f0f7ff" icon={<ClockCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Scheduled" value={scheduledCount} sub="View scheduled" color="#722ed1" bg="#f5eeff" icon={<CalendarOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <StatCard title="Cancelled" value={cancelledCount} sub="View cancelled" color="#f5222d" bg="#fff1f0" icon={<CloseCircleOutlined />} />
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '6px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={17}>
                        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} md={5}>
                                    <Input
                                        allowClear
                                        placeholder="Search count no, warehouse..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Status" options={statusOptions}
                                        value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Warehouses" options={warehouseOptions}
                                        value={selectedWarehouse} onChange={setSelectedWarehouse} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Count Types" options={typeOptions}
                                        value={selectedType} onChange={setSelectedType} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <RangePicker style={{ width: "100%" }} format="DD-MMM-YYYY" value={dateRange} onChange={setDateRange} />
                                </Col>
                                <Col xs={12} md={3}>
                                    <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            rowKey="id"
                            dataSource={filteredCnt.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))}
                            columns={columns}
                        />
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Count Summary (This Month)</Typography.Text>
                            <div style={{ marginTop: 10 }}>
                                {[
                                    ["Total Counts", thisMonthStats.total, "#333"],
                                    ["Completed", completedCount, "#52c41a"],
                                    ["In Progress", inProgressCount, "#1677ff"],
                                    ["Scheduled", scheduledCount, "#722ed1"],
                                    ["Cancelled", cancelledCount, "#f5222d"],
                                ].map(([label, value, color], i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <Typography.Text type="secondary">{label}</Typography.Text>
                                        <Typography.Text strong style={{ color }}>{value}</Typography.Text>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Variance Summary (This Month)</Typography.Text>
                            <div style={{ marginTop: 10 }}>
                                {[
                                    ["Total Variance Value", `₹ ${thisMonthStats.totalValue.toLocaleString('en-IN')}`, thisMonthStats.totalValue >= 0 ? '#52c41a' : '#f5222d'],
                                    ["Positive Variance", `+ ₹ ${thisMonthStats.positive.toLocaleString('en-IN')}`, "#52c41a"],
                                    ["Negative Variance", `₹ ${thisMonthStats.negative.toLocaleString('en-IN')}`, "#f5222d"],
                                ].map(([label, value, color], i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <Typography.Text type="secondary">{label}</Typography.Text>
                                        <Typography.Text strong style={{ color }}>{value}</Typography.Text>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Count Type Breakdown</Typography.Text>
                            {typeSummaryTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📋</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                    <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${typeSummaryTotal ? Math.round((v / typeSummaryTotal) * 100) : 0}%)`, n]} />
                                                <Pie data={typeSummaryData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}>
                                                    {typeSummaryData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{typeSummaryTotal}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {typeSummaryData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({typeSummaryTotal ? Math.round((e.value / typeSummaryTotal) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Typography.Text strong>Recent Counts</Typography.Text>
                            </Space>
                            <div style={{ marginTop: 12 }}>
                                {recentCnt.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <span style={{ color: '#1677ff', cursor: 'pointer' }}
                                            onClick={() => navigate('/inventory/stock-count/view-stock-count', { state: { id: s.id, action: 'View', enableEdit: false, from: '/inventory/stock-count' } })}
                                        >{s.count_no}</span>
                                        <span style={{ color: '#888' }}>{s.count_date ? new Date(s.count_date).toLocaleDateString('en-GB') : '-'}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default StockCountDashboard;